import { ColumnDef } from "@tanstack/react-table";
import classNames from "classnames";
import { useLanguage } from "hooks";
import { ProductCell, ProductColorLabel } from "modules/products";
import moment from "moment";
/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ItemsTable } from "ui";

import { InventoryInsightsListItem } from "../../insights.types";
import { InventoryInsightsTableProps } from "./InventoryInsightsTable.types";

import styles from "./InventoryInsightsTable.module.scss";

const InventoryInsightsTable = ({
  products,
  sorting,
  setSorting,
  isLoading
}: InventoryInsightsTableProps) => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const columns: Array<
    ColumnDef<InventoryInsightsListItem, InventoryInsightsListItem[]>
  > = useMemo(
    () => [
      {
        header: t("Product"),
        size: 32,
        minSize: 32,
        cell: ({ row }) => (
          <ProductCell
            image={products[row.index].image}
            name={products[row.index].name}
            imageClassName={styles.table_productImage}
            subtitle={
              <>
                <span>{products[row.index].sku}</span>
                <span>·</span>
                <ProductColorLabel
                  colorValue={products[row.index].colorAttributeValue.value}
                />
              </>
            }
          />
        )
      },
      {
        id: "lastSaleAt",
        accessorKey: "lastSaleAt",
        header: t("Last Sale"),
        size: 12,
        minSize: 12,
        cell: ({ row }) => (
          <pre>
            {`${moment(products[row.index].lastSaleAt).format("DD/MM/YYYY")}`}
            {"\n"}
            {`${moment(products[row.index].lastSaleAt).format("HH:mm")}`}
          </pre>
        )
      },
      {
        id: "buyingPrice",
        accessorKey: "buyingPrice",
        header: t("Buying Price"),
        size: 13,
        minSize: 13,
        cell: ({ row }) =>
          products[row.index].buyingPrice.toLocaleString("en-US")
      },
      {
        id: "standardPrice",
        accessorKey: "standardPrice",
        header: t("Standard Price"),
        size: 13,
        minSize: 13,
        cell: ({ row }) =>
          products[row.index].standardPrice.toLocaleString("en-US")
      },
      {
        header: t("Sold Range"),
        size: 17,
        minSize: 17,
        cell: ({ row }) =>
          `${products[row.index].minSellingPrice.toLocaleString("en-US")} - ${products[row.index].maxSellingPrice.toLocaleString("en-US")}`
      },
      {
        header: t("Items Sold"),
        size: 5,
        minSize: 5,
        cell: ({ row }) => products[row.index].itemsSold
      },
      {
        header: t("In Stock"),
        size: 14,
        minSize: 14,
        cell: ({ row }) => {
          const isSoldOut = products[row.index].availableItemsCount === 0;

          return (
            <div
              className={classNames(styles.table_inStockLabel, {
                [styles.table_inStockLabel__soldOut]: isSoldOut
              })}
            >
              {isSoldOut
                ? t("Sold Out")
                : `${products[row.index].availableItemsCount} ${t("Left")}`}
            </div>
          );
        }
      }
    ],
    [products, currentLanguage]
  );

  return (
    <ItemsTable
      className={styles.table}
      columns={columns}
      data={products}
      sorting={sorting}
      skeletonHeight={60}
      setSorting={setSorting}
      isLoading={isLoading}
      getLinkUrl={({ subSubCategoryId, productId, id }) => {
        return `/products/details/${subSubCategoryId}/${productId}/${id}/boxes`;
      }}
      linkTarget="_blank"
    />
  );
};

export default InventoryInsightsTable;
